import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useInstitutionsStore } from '@/stores/institutions';
import { useToastStore } from '@/stores/toast';
import { ERouterNames, TFormBuilderEditSchemaCamel } from '@/types';

/**
 * Hook with the visit note template submit handlers.
 * `onSubmit` creates a new template (`formBuilderId` is "empty") or updates the existing one,
 * then navigates to Visit Notes Templates. If the request fails or the template to update
 * isn't found, an error toast is shown and the user stays on the page.
 * @returns The `onSubmit` and `onError` handlers for react-hook-form's `handleSubmit`.
 */
export const useVisitNoteSubmit = () => {
  const { formBuilderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setToast = useToastStore((state) => state.setToast);
  const currentInstitutionId = useInstitutionsStore(
    (state) => state.currentInstitution?.id ?? '',
  );
  const {
    getVisitNoteTemplateById,
    createVisitNotesTemplate,
    updateVisitNotesTemplate,
  } = useInstitutionsStore((state) => ({
    createVisitNotesTemplate: state.createVisitNotesTemplate,
    getVisitNoteTemplateById: state.getVisitNoteTemplateById,
    updateVisitNotesTemplate: state.updateVisitNotesTemplate,
  }));

  const showErrorToast = () =>
    setToast({
      title: t('error'),
      description: t('something-went-wrong-please-try-again'),
      variant: 'error',
    });

  const onSubmit = async (data: TFormBuilderEditSchemaCamel) => {
    if (!formBuilderId) return;
    try {
      if (formBuilderId !== 'empty') {
        const value = getVisitNoteTemplateById(formBuilderId.toString());
        if (!value) {
          showErrorToast();
          return;
        }
        await updateVisitNotesTemplate(value.id, {
          data,
        });
      } else {
        await createVisitNotesTemplate(currentInstitutionId, {
          isActive: true,
          data,
        });
      }
      navigate(ERouterNames.VISIT_NOTES_TEMPLATES);
    } catch (error) {
      console.error(error);
      showErrorToast();
    }
  };

  const onError = (errors: FieldErrors<TFormBuilderEditSchemaCamel>) =>
    console.error('❌ ERRORS:', errors);

  return { onSubmit, onError };
};
